import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Typography,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { teamService, Team, CreateTeamData } from '../services/teams';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ResizableTableHeader } from '../components/ResizableTableHeader';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import '../styles/resizable-table.css';

const { Title } = Typography;

export const Teams: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [form] = Form.useForm<CreateTeamData>();

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const response = await teamService.getAll();
      setTeams(response.data || []);
    } catch {
      message.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTeam(null);
    form.resetFields();
    form.setFieldsValue({ enabled: true });
    setModalVisible(true);
  };

  const handleEdit = (team: Team) => {
    setEditingTeam(team);
    form.setFieldsValue({
      ...team,
      enabled: team.enabled,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await teamService.delete(id);
      message.success('Team deleted');
      fetchTeams();
    } catch {
      message.error('Failed to delete team');
    }
  };

  const handleSubmit = async (values: CreateTeamData) => {
    const payload = {
      ...values,
      enabled: values.enabled ?? true,
    };

    try {
      if (editingTeam) {
        await teamService.update(editingTeam.id, payload);
        message.success('Team updated');
      } else {
        await teamService.create(payload);
        message.success('Team created');
      }
      setModalVisible(false);
      fetchTeams();
    } catch {
      message.error(editingTeam ? 'Failed to update team' : 'Failed to create team');
    }
  };

  const baseColumns = [
    { 
      title: 'Name', 
      dataIndex: 'name', 
      key: 'name', 
      width: 200,
      sorter: (a: Team, b: Team) => (a.name || '').localeCompare(b.name || ''),
    },
    { 
      title: 'Contact Number', 
      dataIndex: 'contact_number', 
      key: 'contact_number', 
      width: 180,
      sorter: (a: Team, b: Team) => (a.contact_number || '').localeCompare(b.contact_number || ''),
    },
    { 
      title: 'Email', 
      dataIndex: 'email', 
      key: 'email', 
      width: 250,
      sorter: (a: Team, b: Team) => (a.email || '').localeCompare(b.email || ''),
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 120,
      sorter: (a: Team, b: Team) => (a.enabled ? 1 : 0) - (b.enabled ? 1 : 0),
      render: (enabled: boolean) => (enabled ? 'Yes' : 'No'),
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const { columns: resizableColumns } = useResizableColumns(visibleColumns);

  const memoizedColumns = useMemo(
    () =>
      resizableColumns.map((col) => ({
        ...col,
        width: col.width, // Preserve width
        onHeaderCell: col.onHeaderCell, // Preserve onHeaderCell for resizing
        getSearchValue:
          col.dataIndex === 'name'
            ? (record: Team) => record.name
            : col.dataIndex === 'contact_number'
            ? (record: Team) => record.contact_number
            : col.dataIndex === 'email'
            ? (record: Team) => record.email
            : col.dataIndex === 'enabled'
            ? (record: Team) => (record.enabled ? 'Yes' : 'No')
            : (undefined as unknown as (record: Team) => string | number | null | undefined),
      })),
    [resizableColumns]
  );

  const { columnsWithSearch, filteredData } = useTableColumnSearch<Team>(teams, memoizedColumns as any);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            Teams
          </Title>
          <Space>
            <ColumnVisibilityToggle
              columns={columnConfig}
              visibleKeys={visibleKeys}
              onChange={handleColumnVisibilityChange}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              New Team
            </Button>
          </Space>
        </div>

        <Table
          columns={columnsWithSearch}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={false}
          components={{
            header: {
              cell: ResizableTableHeader,
            },
          }}
          footer={() => (
            <div style={{ textAlign: 'left', padding: '8px 0' }}>
              Total: {filteredData.length} {filteredData.length === 1 ? 'team' : 'teams'}
            </div>
          )}
          onRow={(record) => ({
            onClick: () => handleEdit(record),
            style: { cursor: 'pointer' },
          })}
        />

      <Modal
        title={editingTeam ? 'Edit Team' : 'New Team'}
        open={modalVisible}
        footer={null}
        onCancel={() => setModalVisible(false)}
        width={520}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Team Name"
            rules={[{ required: true, message: 'Please enter team name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="contact_number" label="Contact Number">
            <Input />
          </Form.Item>

          <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Enter a valid email' }]}>
            <Input />
          </Form.Item>

          <Form.Item name="enabled" label="Enabled" valuePropName="checked" initialValue>
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
              {editingTeam && (
                <Button 
                  danger 
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this team?')) {
                      handleDelete(editingTeam.id);
                      setModalVisible(false);
                    }
                  }}
                >
                  Delete
                </Button>
              )}
              <Button type="primary" htmlType="submit">
                {editingTeam ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

